import {DeadlineListElementType, DeadlineListMapType, DeadlineSearchParamType} from "../interface";
import {Dispatch, SetStateAction} from "react";
import EmptyTableRow from "../../../../common/EmptyTableRow";
import {PaginationMain} from "../../../../../components/common/pagination/PaginationMain";
import {useModal} from "../../../../../hooks";
import {useUpdateDeadlineProgress} from "../hooks/useUpdateDeadlineProgress";
import useGoToNavigation from "../../../../../util/useGotoNavigation";
import {useIssueTax} from "../hooks/useIssueTax";
import {useDownloadDeadlineFile} from "../hooks/useDownloadDeadlineFile";
import DisplayNumber from "../../../../../components/common/number/DisplayNumber";
import AuthorityChecker from "../../../../../components/common/authorityGuard/AuthorityChecker";

interface PropsType {
    searchParam: DeadlineSearchParamType,
    deadline: DeadlineListMapType,
    selectedItems: DeadlineListElementType[],
    setSelectedItems: Dispatch<SetStateAction<DeadlineListElementType[]>>
}

export default function List({searchParam, deadline, selectedItems, setSelectedItems}: PropsType) {
    const {handleModal} = useModal();
    const {goToNavigation} = useGoToNavigation();
    const {mutate: downloadDeadline} = useDownloadDeadlineFile();
    const deadlineList = deadline.list ?? []
    const deadlineTotalCount = deadline.totalCount ?? 0

    const isAllCheck = deadlineList.length !== 0 && deadlineList.every(el => selectedItems.includes(el))

    const closeCallback = () => setSelectedItems([])
    const {mutate: mutateUpdateDeadlineProgress} = useUpdateDeadlineProgress({closeCallback: closeCallback})
    const {mutate: mutateIssueTax} = useIssueTax({closeCallback: closeCallback})

    const clickAllCheck = () => {
        if (isAllCheck) {
            const list = selectedItems.filter(el => !deadlineList.includes(el))

            setSelectedItems(list)
        } else {
            // @ts-ignore
            const list = [...new Set([...selectedItems, ...deadlineList])]

            setSelectedItems(list)
        }
    }

    const clickCheck = (el: DeadlineListElementType) => {
        if (selectedItems.includes(el)) {
            const list = selectedItems.filter(el2 => el.newShipmentId !== el2.newShipmentId)

            setSelectedItems(list)
        } else {
            setSelectedItems([...selectedItems, el])
        }
    }

    const clickRow = (el: DeadlineListElementType) => {
        goToNavigation(`order/deadline/detail/${el.newShipmentId}`, {}, false)
    }

    const btnToggleFinish = (key: "Y" | "N") => {
        const list = selectedItems.map(el => ({...el, endFlag: key}))

        handleModal({
            isOpen: true,
            contents: key === "Y" ? "마감 처리하시겠습니까?" : "마감 취소하시겠습니까?",
            type: "confirm",
            callback: () => mutateUpdateDeadlineProgress(list)
        })
    }

    const btnInvoice = () => {
        mutateIssueTax(selectedItems)
    }

    // 선택된 항목이 없거나, 선택된 항목 중에 상태가 Y(마감)인게 있으면 마감 버튼 비활성화
    const isBtnFinishDisabledY = selectedItems.length === 0 || selectedItems.some(el => el.endFlag === "Y")

    // 선택된 항목이 없거나, 선택된 항목 중에 상태가 N(미마감)인게 있으면 마감취소 버튼 비활성화
    const isBtnFinishDisabledN = selectedItems.length === 0 || selectedItems.some(el => el.endFlag === "N")

    // 선택된 항목이 없거나, 선택된 항목 중에 상태 N(미마감)인게 있거나 매출처가 다른 항목이 있으면 세금계산서 발행 버튼 비활성화
    const isBtnInvoiceDisabled = selectedItems.length === 0 || selectedItems.some(el => el.endFlag === "N") || selectedItems.some(el => el.customerName !== selectedItems.at(0)?.customerName)

    return <div className="content_wrap">
        <div className="table_top">
            <h6 className="table_stats">마감 목록(<span>{deadlineTotalCount}</span>건)</h6>
            <div className="table_toolbar">
                <button className="btn btn-dark w-150px px-0" onClick={() => downloadDeadline(searchParam)}>
                    <img src={process.env.PUBLIC_URL + "/assets/images/icon_export.png"} alt=""/>엑셀 내보내기
                </button>
                <AuthorityChecker authorityNames={['taxWriteYn']}>
                    <button className="btn btn-primary ms-40px" onClick={btnInvoice}
                            disabled={isBtnInvoiceDisabled}>세금계산서 발행
                    </button>
                </AuthorityChecker>
                <AuthorityChecker authorityNames={['endWriteYn']}>
                    <button className="btn btn-info color2 w-80px p-0" onClick={() => btnToggleFinish("Y")}
                            disabled={isBtnFinishDisabledY}>마감
                    </button>
                    <button className="btn btn-outline-info w-80px p-0" onClick={() => btnToggleFinish("N")}
                            disabled={isBtnFinishDisabledN}>마감취소
                    </button>
                </AuthorityChecker>
            </div>
        </div>

        <div className="table_scroll">
            <table className="table table-bordered my_table">
                <thead>
                <tr>
                    <th style={{width: "3%"}}>
                        <div className="checkbox_outer">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={isAllCheck}
                                onClick={clickAllCheck}
                            />
                        </div>
                    </th>
                    <th style={{width: "8%"}}>수주일자</th>
                    <th style={{width: "8%"}}>마감일자</th>
                    <th style={{width: "7%"}}>마감구분</th>
                    <th style={{width: "8%"}}>담당자</th>
                    <th style={{width: "20%"}}>매출처</th>
                    <th style={{width: "5%"}}>환종</th>
                    <th style={{width: "6%"}}>환율</th>
                    <th style={{width: "8%"}}>공급가액</th>
                    <th style={{width: "8%"}}>부가세</th>
                    <th style={{width: "8%"}}>금액</th>
                    <th style={{width: "6%"}}>상태</th>
                </tr>
                </thead>
                <tbody>
                {deadlineList.length !== 0 ?
                    deadlineList.map(el =>
                        <tr className={`clickable ${selectedItems.map(el => el.newShipmentId).includes(el.newShipmentId) ? "lightblue" : ""}`}
                            onClick={() => clickRow(el)}>
                            <td className="text-center" onClick={e => e.stopPropagation()}>
                                <div className="checkbox_outer">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={selectedItems.map(el => el.newShipmentId).includes(el.newShipmentId)}
                                        onClick={() => clickCheck(el)}/>
                                </div>
                            </td>
                            <td className="text-center">{el.newDtShipment}</td>
                            <td className="text-center">{el.endDt}</td>
                            <td className="text-center">{el.endType}</td>
                            <td className="text-center">{el.owningUserName}</td>

                            <td className="text-center">{el.customerName}</td>
                            <td className="text-center">{el.currencyName}</td>
                            <td className="text-end"><DisplayNumber value={el.newDExchangeRate}/></td>
                            <td className="text-end"><DisplayNumber value={el.newDAmount}/></td>
                            <td className="text-end"><DisplayNumber value={el.taxTotal}/></td>
                            <td className="text-end"><DisplayNumber value={el.totalAmount}/></td>
                            <td className="text-center">{el.endState}</td>
                        </tr>
                    )
                    : <EmptyTableRow colSpan={12}/>}
                </tbody>
            </table>
        </div>

        <PaginationMain totalCount={deadlineTotalCount}/>
    </div>
}